import React, { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert } from "../../../ui/primitives/Alert";
import { Button } from "../../../ui/primitives/Button";
import { Card } from "../../../ui/primitives/Card";
import { Checkbox } from "../../../ui/primitives/Checkbox";
import { EmptyState } from "../../../ui/primitives/EmptyState";
import { Input } from "../../../ui/primitives/Input";
import { Stack } from "../../../ui/primitives/Stack";
import { Text } from "../../../ui/primitives/Text";
import { useTokens } from "../../../core/theme/TokensProvider";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import {
  ReplaceShiftConfigPayload,
  ShiftBlock,
  SHIFT_BLOCKS,
  SHIFT_BLOCK_LABELS,
  ShiftConfigDefinition,
} from "../../../model/members/ShiftChecklist";

// CRM > Shift Checklists. Owns the per-client shift checklist templates that Staff
// Daily works through. Club-owner-managed (admin:club-owner or Fortius admin), so it
// lives on its own route rather than inside the Fortius settings wizard.
const ShiftChecklistsManager: React.FC = () => {
  const service = useClientConfigService();
  const tokens = useTokens();
  const [draft, setDraft] = useState<ShiftConfigDefinition[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  // Client-side ids for new rows are negative so a save can tell them from server ids.
  const nextKey = useRef<number>(-1);
  const newId = (): number => {
    const k = nextKey.current;
    nextKey.current -= 1;
    return k;
  };

  const load = useCallback(async () => {
    try {
      setDraft(await service.getShiftConfig());
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Failed to load shift checklists.");
    }
  }, [service]);

  useEffect(() => {
    load();
  }, [load]);

  const updateShift = (id: number, patch: Partial<ShiftConfigDefinition>) =>
    setDraft((d) => d.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  // newId() mutates a ref, so it's called outside the state updater: React 18 StrictMode
  // invokes updaters twice in dev, which would burn (and mis-number) an id per click.
  const addShift = () => {
    const id = newId();
    setDraft((d) => [
      ...d,
      { id, name: "", startTime: null, endTime: null, position: d.length, isUrgent: false, items: [] },
    ]);
  };

  const removeShift = (id: number) => setDraft((d) => d.filter((s) => s.id !== id));

  const addItem = (shiftId: number, block: ShiftBlock) => {
    const id = newId();
    setDraft((d) =>
      d.map((s) =>
        s.id === shiftId
          ? {
              ...s,
              items: [
                ...s.items,
                { id, block, label: "", position: s.items.filter((i) => i.block === block).length },
              ],
            }
          : s
      )
    );
  };

  const updateItemLabel = (shiftId: number, itemId: number, label: string) =>
    setDraft((d) =>
      d.map((s) =>
        s.id === shiftId ? { ...s, items: s.items.map((i) => (i.id === itemId ? { ...i, label } : i)) } : s
      )
    );

  const removeItem = (shiftId: number, itemId: number) =>
    setDraft((d) =>
      d.map((s) => (s.id === shiftId ? { ...s, items: s.items.filter((i) => i.id !== itemId) } : s))
    );

  const save = async () => {
    for (const s of draft) {
      if (!s.name.trim()) {
        message.error("Every shift needs a name.");
        return;
      }
      for (const i of s.items) {
        if (!i.label.trim()) {
          message.error("Every checklist item needs a label.");
          return;
        }
      }
    }

    const payload: ReplaceShiftConfigPayload = {
      shifts: draft.map((s, si) => ({
        id: s.id > 0 ? s.id : null,
        name: s.name.trim(),
        startTime: s.startTime?.trim() ? s.startTime.trim() : null,
        endTime: s.endTime?.trim() ? s.endTime.trim() : null,
        position: si,
        isUrgent: s.isUrgent,
        items: s.items.map((i, ii) => ({
          id: i.id > 0 ? i.id : null,
          block: i.block,
          label: i.label.trim(),
          position: ii,
        })),
      })),
    };

    setSaving(true);
    try {
      setDraft(await service.replaceShiftConfig(payload));
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Failed to save shift checklists.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="shift-checklists-page" style={{ padding: tokens.space[5] }}>
      <Text variant="h3">Shift checklists</Text>
      <div style={{ marginTop: tokens.space[1] }}>
        <Text variant="caption">
          Configure the shifts and the Opening / During / Lock-up checklists staff work through on
          Staff Daily.
        </Text>
      </div>

      {loadError ? (
        <div style={{ margin: `${tokens.space[4]}px 0` }}>
          <Alert type="error" message={loadError} data-testid="shift-checklists-error" />
        </div>
      ) : null}

      <Stack gap={4} style={{ marginTop: tokens.space[4] }}>
        {draft.length === 0 && !loadError ? (
          <EmptyState
            description="No shifts yet. Add one to get started."
            data-testid="shift-checklists-empty"
          />
        ) : null}

        {draft.map((shift) => (
          <Card
            key={shift.id}
            data-testid={`shift-card-${shift.id}`}
            size="small"
            header={
              <Stack direction="horizontal" gap={2} align="center" wrap justify="space-between">
                <Stack direction="horizontal" gap={2} align="center" wrap>
                  <Input
                    data-testid={`shift-name-${shift.id}`}
                    placeholder="Shift name (e.g. Reception)"
                    value={shift.name}
                    style={{ width: 220 }}
                    onChange={(e) => updateShift(shift.id, { name: e.target.value })}
                  />
                  <Input
                    data-testid={`shift-start-${shift.id}`}
                    placeholder="Start HH:mm"
                    value={shift.startTime ?? ""}
                    style={{ width: 110 }}
                    onChange={(e) => updateShift(shift.id, { startTime: e.target.value })}
                  />
                  <Input
                    data-testid={`shift-end-${shift.id}`}
                    placeholder="End HH:mm"
                    value={shift.endTime ?? ""}
                    style={{ width: 110 }}
                    onChange={(e) => updateShift(shift.id, { endTime: e.target.value })}
                  />
                  <Checkbox
                    checked={shift.isUrgent}
                    onChange={(checked) => updateShift(shift.id, { isUrgent: checked })}
                  >
                    Urgent
                  </Checkbox>
                </Stack>
                <Button
                  variant="link"
                  icon={<DeleteOutlined />}
                  style={{ color: tokens.color.danger }}
                  data-testid={`shift-remove-${shift.id}`}
                  onClick={() => removeShift(shift.id)}
                />
              </Stack>
            }
          >
            {SHIFT_BLOCKS.map((block) => (
              <div key={block} style={{ marginBottom: tokens.space[3] }}>
                <Text variant="body" weight="semibold">
                  {SHIFT_BLOCK_LABELS[block]}
                </Text>
                <Stack gap={2} style={{ marginTop: tokens.space[1] }}>
                  {shift.items
                    .filter((i) => i.block === block)
                    .map((item) => (
                      <Stack key={item.id} direction="horizontal" gap={2} align="center">
                        <Input
                          data-testid={`shift-item-label-${item.id}`}
                          placeholder="Checklist item"
                          value={item.label}
                          style={{ width: 360 }}
                          onChange={(e) => updateItemLabel(shift.id, item.id, e.target.value)}
                        />
                        <Button
                          variant="link"
                          icon={<DeleteOutlined />}
                          style={{ color: tokens.color.danger }}
                          data-testid={`shift-item-remove-${item.id}`}
                          onClick={() => removeItem(shift.id, item.id)}
                        />
                      </Stack>
                    ))}
                  <div>
                    <Button
                      variant="default"
                      size="sm"
                      icon={<PlusOutlined />}
                      data-testid={`shift-add-item-${shift.id}-${block}`}
                      onClick={() => addItem(shift.id, block)}
                    >
                      Add item
                    </Button>
                  </div>
                </Stack>
              </div>
            ))}
          </Card>
        ))}
      </Stack>

      <Stack direction="horizontal" gap={2} style={{ marginTop: tokens.space[5] }}>
        <Button icon={<PlusOutlined />} onClick={addShift} data-testid="shift-add-shift">
          Add shift
        </Button>
        {/* A save after a failed load would PUT an empty template over the real one. */}
        <Button
          variant="primary"
          loading={saving}
          disabled={loadError != null}
          onClick={save}
          data-testid="shift-save"
        >
          Save shift checklists
        </Button>
      </Stack>
    </div>
  );
};

export default ShiftChecklistsManager;
